import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useClientTaxCases } from '../hooks/useClientTaxCases';
import { getUserProfile } from '../services/firestoreService';
import { TaxStatus, UserProfile } from '../types';

interface ClientDashboardProps {
  onCaseSelect: (caseId: string) => void;
  onNavigate: (view: string) => void;
}

const ClientDashboard: React.FC<ClientDashboardProps> = ({ onCaseSelect, onNavigate }) => {
  const { user, userDoc, firmId, firmSettings, logout } = useAuth();
  const { cases, loading, error } = useClientTaxCases(user?.uid);
  const [taxPro, setTaxPro] = useState<UserProfile | any>(null);
  const brandColor = firmId === 'hannah-financial-demo' ? '#27b3e6' : '#0ea5e9';

  useEffect(() => {
    const fetchTaxPro = async () => {
        // Fix: Use firmId from context when calling getUserProfile
        if (cases.length > 0 && cases[0].assignedStaffId && firmId) {
            const staffId = cases[0].assignedStaffId;
            
            if (staffId.startsWith('demo-')) {
                setTaxPro({
                    displayName: 'Bob Wilson, Tax Pro',
                    role: 'STAFF',
                    avatarUrl: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-1.2.1&auto=format&fit=crop&w=256&q=80',
                    email: 'bob.wilson@hannahfinancial.com'
                });
                return;
            }

            try {
                // Fix: pass firmId as the first argument
                const profile = await getUserProfile(firmId, staffId);
                if (profile) setTaxPro(profile);
            } catch (e) {
                console.error("Failed to load tax pro profile", e);
            }
        }
    };

    if (!loading) {
        fetchTaxPro();
    }
  }, [cases, loading, firmId]);

  const getStatusColor = (status: TaxStatus) => {
    switch (status) {
      case TaxStatus.INFO_RECEIVED: return 'bg-blue-50 text-blue-700 border-blue-200';
      case TaxStatus.IN_PROGRESS: return 'bg-purple-50 text-purple-700 border-purple-200';
      case TaxStatus.IN_REVIEW: return 'bg-amber-50 text-amber-700 border-amber-200';
      case TaxStatus.READY_TO_SIGN: return 'bg-orange-50 text-orange-700 border-orange-200';
      case TaxStatus.FILED: return 'bg-indigo-50 text-indigo-700 border-indigo-200';
      case TaxStatus.ACCEPTED: return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      default: return 'bg-slate-50 text-slate-700 border-slate-200';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-8 h-8 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-700 border border-red-200 rounded-lg m-4">
        {error}
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-2 md:p-2">
      <div className="flex flex-col lg:flex-row gap-8">
        
        <div className="flex-1">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-slate-800">My Tax Returns</h1>
            <p className="text-slate-500">Track the status of your filings and access past returns.</p>
          </div>

          {cases.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-dashed border-slate-300">
              <div className="text-4xl mb-4">📂</div>
              <h3 className="text-lg font-medium text-slate-800">No Returns Found</h3>
              <p className="text-slate-500 max-w-sm mx-auto mt-2">
                We haven't started a tax return for you yet. Contact your Tax Pro to initiate a new year.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {cases.map((taxCase) => {
                // Don't show ID folder in this list, only returns
                if (taxCase.year > 9000) return null;
                return (
                <div 
                  key={taxCase.id}
                  onClick={() => onCaseSelect(taxCase.id)}
                  className="group bg-white rounded-xl border border-slate-200 p-6 shadow-sm hover:shadow-md hover:border-brand-300 transition-all cursor-pointer relative overflow-hidden"
                >
                  <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                    <span className="text-6xl font-bold text-slate-800">{taxCase.year}</span>
                  </div>

                  <div className="relative z-10">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-slate-800">{taxCase.year} Return</h3>
                        <p className="text-sm text-slate-500">{taxCase.filingType}</p>
                      </div>
                    </div>

                    <div className="mb-6">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border ${getStatusColor(taxCase.status)}`}>
                        {taxCase.status}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-xs text-slate-400 pt-4 border-t border-slate-100">
                      <span>Last updated: {taxCase.lastUpdated?.toDate?.().toLocaleDateString() || 'N/A'}</span>
                      <span className="text-brand-600 font-medium group-hover:underline flex items-center gap-1">
                        View Details →
                      </span>
                    </div>
                  </div>
                </div>
              )})}
            </div>
          )}

          <div className="mt-12">
            <div className="flex items-center gap-3 mb-4">
              <h2 className="text-xl font-bold text-slate-800">Required Actions</h2>
              <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">2 Pending</span>
            </div>
            
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm divide-y divide-slate-100 overflow-hidden">
               <div className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-slate-50 transition-colors">
                  <div className="flex items-start gap-4">
                     <div className="w-10 h-10 rounded-full bg-red-100 text-red-600 flex-shrink-0 flex items-center justify-center">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                     </div>
                     <div>
                        <h4 className="font-semibold text-slate-800">Sign e-File Authorization (8879)</h4>
                        <p className="text-sm text-slate-500 mt-1">2024 Individual Return • Due by April 15</p>
                     </div>
                  </div>
                  <button className="text-sm font-medium text-white bg-[#a40000] px-5 py-2.5 rounded-lg hover:bg-[#800000] shadow-sm whitespace-nowrap transition-colors">
                     Review & Sign
                  </button>
               </div>
               
               <div className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-slate-50 transition-colors">
                  <div className="flex items-start gap-4">
                     <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-600 flex-shrink-0 flex items-center justify-center">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                     </div>
                     <div>
                        <h4 className="font-semibold text-slate-800">Upload Driver's License</h4>
                        <p className="text-sm text-slate-500 mt-1">Identity verification needed for e-filing</p>
                     </div>
                  </div>
                  <button className="text-sm font-medium text-slate-700 bg-white border border-slate-300 px-5 py-2.5 rounded-lg hover:bg-slate-50 hover:text-slate-900 shadow-sm whitespace-nowrap transition-colors">
                     Upload Document
                  </button>
               </div>
            </div>
          </div>
        </div>

        <div className="w-full lg:w-80 flex-shrink-0">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 sticky top-6">
            <h3 className="font-bold text-slate-800 mb-5 flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-100 text-lg">📢</span> 
              Firm Updates
            </h3>
            
            <div className="space-y-6">
              <div className="border-l-2 border-brand-500 pl-4">
                <p className="text-xs text-slate-400 font-medium mb-1">Feb 14, 2025</p>
                <h4 className="text-sm font-semibold text-slate-800">2024 Filing Deadline Approaching</h4>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                  The deadline for S-Corp and Partnership returns is March 15th. Please ensure all documents are uploaded by March 1st.
                </p>
              </div>

              <div className="border-l-2 border-slate-300 pl-4">
                <p className="text-xs text-slate-400 font-medium mb-1">Feb 01, 2025</p>
                <h4 className="text-sm font-semibold text-slate-800">New Secure Portal Features</h4>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                  We have upgraded our document encryption for safer exchanges. Contact us via our main office line if you have any questions.
                </p>
              </div>

              <div className="border-l-2 border-slate-300 pl-4">
                <p className="text-xs text-slate-400 font-medium mb-1">Jan 02, 2025</p>
                <h4 className="text-sm font-semibold text-slate-800">Welcome to Tax Season 2024</h4>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                  Our office hours are extended to 8 AM - 7 PM, Monday through Saturday, until April 15th.
                </p>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-slate-100">
               <h4 className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2">
                  <span>💼</span> My Tax Pro
               </h4>
               
               {taxPro ? (
                 <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                    <div className="flex items-center gap-3 mb-3">
                        <img 
                            src={taxPro.avatarUrl} 
                            alt={taxPro.displayName} 
                            className="w-12 h-12 rounded-full border-2 border-white shadow-sm object-cover"
                        />
                        <div>
                            <p className="font-bold text-slate-800 text-sm">{taxPro.displayName}</p>
                            <p className="text-xs text-slate-500">{firmSettings.name}</p>
                        </div>
                    </div>
                 </div>
               ) : (
                 <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 text-center">
                    <div className="w-10 h-10 bg-slate-200 rounded-full mx-auto mb-2 flex items-center justify-center text-slate-400">?</div>
                    <p className="text-xs text-slate-500 mb-2">Matching you with a pro...</p>
                    <p className="text-[10px] text-slate-400">Your return is being reviewed for assignment.</p>
                 </div>
               )}

                <div className="flex flex-col gap-2 mt-4">
                  <button 
                      onClick={() => onNavigate('account')}
                      className="w-full text-center text-sm font-medium text-slate-600 bg-white hover:bg-slate-50 py-2 rounded-lg border border-slate-200 transition-colors shadow-sm"
                  >
                      👤 My Profile
                  </button>
                  <button 
                      onClick={logout}
                      className="w-full text-center text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-red-500 py-1 transition-colors"
                  >
                      Logout & Switch Role
                  </button>
                </div>
            </div>

          </div>
        </div>

      </div>
    </div>
  );
};

export default ClientDashboard;